import { AppBar, Box, IconButton, Menu, MenuItem, Toolbar, styled } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import * as React from 'react';
import { observer } from 'mobx-react-lite';

import { useStores } from 'app/stores/config/UseStores';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: theme.palette.common.white,
  color: theme.palette.grey[500],
  boxShadow: '0px 2px 1px -1px #D9D9D9, 0px 1px 1px 0px #D9D9D9, 0px 1px 3px 0px #D9D9D9',
}));

const StyledIconButton = styled(IconButton)({
  '& svg': {
    width: 22,
    height: 22,
  },
});

const ImagePlaceholder = styled(Box)({
  alignItems: 'center',
  background: '#fff',
  display: 'flex',
  justifyContent: 'center',
  position: 'relative',
  borderRadius: '100%',
  height: 24,
  marginTop: 0,
  width: 24,
  '& img': {
    borderRadius: '100%',
    objectFit: 'cover',
    height: '100%',
    width: '100%',
  },
});

interface UserMenuProps {
  onLogout: () => void;
  onProfile: () => void;
}

const UserMenu = observer(({ onLogout, onProfile }: UserMenuProps) => {
  const {
    dataStores: { userStore, authStore },
  } = useStores();

  const [anchorMenu, setAnchorMenu] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorMenu);

  const toggleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorMenu(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorMenu(null);
  };

  return (
    <>
      <StyledIconButton
        aria-owns={open ? 'menu-appbar' : undefined}
        aria-haspopup="true"
        onClick={toggleMenu}
        color="inherit"
        size="large"
      >
        <ImagePlaceholder>
          {userStore.profilePhoto ? (
            <img
              src={userStore.profilePhoto}
              alt="profile"
            />
          ) : (
            <AccountCircleOutlinedIcon sx={{ fontSize: 24 }} />
          )}
        </ImagePlaceholder>
      </StyledIconButton>
      <Menu
        id="menu-appbar"
        keepMounted
        anchorEl={anchorMenu}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: { width: 160 },
          },
        }}
      >
        {authStore.isConcierge && (
          <MenuItem
            onClick={() => {
              onProfile();
              closeMenu();
            }}
          >
            Profile
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            onLogout();
            closeMenu();
          }}
        >
          Log Out
        </MenuItem>
      </Menu>
    </>
  );
});

interface HeaderProps {
  onLogout: () => void;
  onProfile: () => void;
}

const Header = ({ onLogout, onProfile }: Omit<HeaderProps, 'onDrawerToggle' | 'isDrawerCollapsed'>) => {
  const {
    uiStores: { layoutStore },
  } = useStores();

  return (
    <StyledAppBar
      position="sticky"
      elevation={0}
    >
      <Toolbar>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: { xs: 'block' } }}>
            <StyledIconButton
              color="inherit"
              aria-label={layoutStore.sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              onClick={layoutStore.toggleSidebar}
              size="large"
            >
              <MenuIcon />
            </StyledIconButton>
          </Box>

          <Box sx={{ marginLeft: 'auto' }}>
            <UserMenu
              onLogout={onLogout}
              onProfile={onProfile}
            />
          </Box>
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
};

export default observer(Header);
